import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import MicIcon from "@mui/icons-material/Mic";
import MicNoneIcon from "@mui/icons-material/MicNone";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import {
  Box,
  Button,
  LinearProgress,
  ListItemButton,
  Popover,
  Tooltip,
} from "@mui/material";
import ZoomVideo, {
  TestMicrophoneReturn,
  TestSpeakerReturn,
} from "@zoom/videosdk";
import { useContext, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ZoomMediaContext } from "../../ZoomMediaProvider";
import Flex from "../../components/Flex";
import Text from "../../components/Text";
import { useLoadingContext } from "../../context/LoadingProvider";
import { RootState } from "../../redux/store";
import { MediaDevice } from "../video-types";
import ButtonWithOptions from "./ButtonWithOptions";

interface CameraButtonProps {
  isStartedAudio: boolean;
  isMuted: boolean;
  isSupportPhone?: boolean;
  isMicrophoneForbidden?: boolean;
  disabled?: boolean;
  audio?: string;
  phoneCountryList?: any[];
  onMicrophoneClick: () => void;
  onPhoneCallClick?: (
    code: string,
    phoneNumber: string,
    name: string,
    option: any
  ) => void;
  onPhoneCallCancel?: (
    code: string,
    phoneNumber: string,
    option: any
  ) => Promise<any>;
  className?: string;
  microphoneList?: MediaDevice[];
  speakerList?: MediaDevice[];
  activeMicrophone?: string;
  activeSpeaker?: string;
  phoneCallStatus?: { text: string; type: string };
  isSecondaryAudioStarted?: boolean;
}

const JoinRoomMicroPhoneButton = (props: CameraButtonProps) => {
  const { isLoading } = useLoadingContext();
  const mediaContext = useContext(ZoomMediaContext);
  const speakerTesterRef = useRef<TestSpeakerReturn>();
  const microphoneTesterRef = useRef<TestMicrophoneReturn>();
  const [inputLevel, setInputLevel] = useState(0);
  const localAudio = ZoomVideo.createLocalAudioTrack();

  if (!mediaContext) {
    throw new Error("ZoomMediaContext is not available.");
  }

  const {
    isStartedAudio,
    isSupportPhone,
    isMuted,
    microphoneList,
    speakerList,
    activeMicrophone,
    activeSpeaker,
    onMicrophoneClick,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  const handleOpenChange = (newAnchorEl: HTMLButtonElement | null) => {
    setAnchorEl(newAnchorEl);
    // onOpenChange?.(Boolean(newAnchorEl));
  };

  const tooltipText = isStartedAudio
    ? isMuted
      ? "unmute"
      : "mute"
    : "start audio";

  const open = Boolean(anchorEl);

  const isMinimizeCall = useSelector(
    (state: RootState) => state.joinRoom.isMinimize
  );

  const onTestMicrophoneClick = () => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
    }

    microphoneTesterRef.current = localAudio.testMicrophone({
      microphoneId: activeMicrophone,
      speakerId: activeSpeaker,
      onAnalyseFrequency: (value) => {
        setInputLevel(Math.min(100, value));
      },
    });
  };

  const onTestSpeakerClick = () => {
    if (microphoneTesterRef.current) {
      microphoneTesterRef.current.destroy();
      microphoneTesterRef.current = undefined;
    }
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
    } else {
      speakerTesterRef.current = localAudio.testSpeaker({
        speakerId: activeSpeaker,
      });
      setIsPlayingAudio(true);
    }
  };

  return (
    <Flex
      sx={
        !isMinimizeCall
          ? {
              bgcolor: "#2d2f31",
              borderRadius: "16px",
            }
          : undefined
      }
    >
      {!isMinimizeCall && (
        <Button
          endIcon={<KeyboardArrowUpOutlinedIcon color="primary" />}
          onClick={(e) => {
            handleOpenChange(e.currentTarget);
          }}
        />
      )}
      <Tooltip title={tooltipText}>
        <div>
          {isSupportPhone ? (
            <></>
          ) : (
            <Button onClick={onMicrophoneClick}>
              {isMuted || isLoading ? (
                <svg
                  id="Component_2275_5"
                  data-name="Component 2275 – 5"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                >
                  <path
                    id="Path_206754"
                    data-name="Path 206754"
                    d="M20,0A20,20,0,1,1,0,20,20,20,0,0,1,20,0Z"
                    fill="#ff6969"
                  />
                  <g id="mic_off_black_24dp" transform="translate(8 8)">
                    <path
                      id="Path_206757"
                      data-name="Path 206757"
                      d="M0,0H24V24H0Z"
                      fill="none"
                    />
                    <path
                      id="Path_206758"
                      data-name="Path 206758"
                      d="M10.7,4.871a1.185,1.185,0,1,1,2.371,0l-.01,3.871,1.788,1.772V4.97a2.953,2.953,0,0,0-5.887-.347L10.7,6.366Zm8.1,6.039H17.126a5.132,5.132,0,0,1-.267,1.624l1.255,1.257A6.452,6.452,0,0,0,18.8,10.911ZM4.393,2.851,3,4.247l5.927,5.94v.723a2.963,2.963,0,0,0,2.963,2.97,2.715,2.715,0,0,0,.642-.079l1.64,1.644a5.421,5.421,0,0,1-2.282.515,5.131,5.131,0,0,1-5.235-5.049H4.976A6.909,6.909,0,0,0,10.9,17.564v3.247h1.976V17.564a7.032,7.032,0,0,0,2.519-.891l4.149,4.158,1.393-1.4Z"
                      transform="translate(0.114 0.076)"
                      fill="#fff"
                    />
                  </g>
                </svg>
              ) : (
                <svg
                  id="Component_2275_1"
                  data-name="Component 2275 – 1"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                >
                  <path
                    id="Path_206754"
                    data-name="Path 206754"
                    d="M20,0A20,20,0,1,1,0,20,20,20,0,0,1,20,0Z"
                    fill="#fff"
                  />
                  <g
                    id="mic_none_black_24dp_1_"
                    data-name="mic_none_black_24dp (1)"
                    transform="translate(8 8)"
                  >
                    <path
                      id="Path_206755"
                      data-name="Path 206755"
                      d="M0,0H24V24H0Z"
                      fill="none"
                    />
                    <path
                      id="Path_206756"
                      data-name="Path 206756"
                      d="M11.952,13.97a2.982,2.982,0,0,0,2.979-2.992V4.992a2.979,2.979,0,1,0-5.959,0v5.985A2.982,2.982,0,0,0,11.952,13.97Zm-.993-8.977a.993.993,0,1,1,1.986,0v5.985a.993.993,0,1,1-1.986,0Zm5.959,5.985a4.966,4.966,0,1,1-9.931,0H5a6.968,6.968,0,0,0,5.959,6.9v3.072h1.986V17.88a6.968,6.968,0,0,0,5.959-6.9Z"
                      transform="translate(0.048 0.019)"
                      fill="rgba(26,26,26,0.8)"
                    />
                  </g>
                </svg>
              )}
            </Button>
          )}
        </div>
      </Tooltip>
      <Popover
        sx={
          {
            //   borderRadius: "40px", //useLess
            //   bgcolor: "red",//To test
          }
        }
        open={open}
        anchorEl={anchorEl}
        onClose={() => handleOpenChange(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Flex>
          <Box width={"100%"}>
            <ButtonWithOptions
              style={{
                width: "20vw",
                borderRadius: "40px",
                border: "1px solid black",
                margin: "3px",
              }}
              fullWidth={false}
              onOpenChange={(open) => {
                if (open && !isMuted) onTestMicrophoneClick();
              }}
              onChange={(val) => {
                if (val) {
                  if (val !== activeMicrophone) {
                    mediaContext?.mediaStream?.switchMicrophone(val);
                    mediaContext?.setActiveMicrophone(val);
                  }
                }
              }}
              value={activeMicrophone}
              options={microphoneList}
              label={
                microphoneList?.find((sp) => sp.deviceId === activeMicrophone)
                  ?.label ?? ""
              }
              rightIcon={<MicNoneIcon />}
              // w={"250px"}
            >
              <Flex>
                <MicIcon />
                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    alignContent: "center",
                    marginRight: "10px",
                  }}
                >
                  <LinearProgress variant="determinate" value={inputLevel} />
                </Box>
              </Flex>
            </ButtonWithOptions>
          </Box>
          <Box width={"100%"}>
            <ButtonWithOptions
              fullWidth={false}
              style={{
                width: "20vw",
                borderRadius: "40px",
                border: "1px solid black",
                margin: "3px",
              }}
              onOpenChange={(open) => {
                if (!open) {
                  speakerTesterRef.current?.stop();
                  setIsPlayingAudio(false);
                }
              }}
              children={
                <ListItemButton
                  onClick={() => {
                    onTestSpeakerClick();
                  }}
                >
                  <Flex
                    justifyContent={"end"}
                    alignContent={"end"}
                    gap={1}
                    sx={{
                      marginRight: "10px",
                      marginTop: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <VolumeUpIcon />
                    <Text> Test Speakers</Text>
                  </Flex>
                </ListItemButton>
              }
              onChange={async (val) => {
                if (val) {
                  if (val !== activeSpeaker) {
                    await mediaContext?.mediaStream?.switchSpeaker(val);
                    mediaContext?.setActiveSpeaker(val);
                  }
                }
              }}
              value={activeSpeaker}
              options={speakerList}
              label={
                speakerList?.find((sp) => sp.deviceId === activeSpeaker)
                  ?.label ?? ""
              }
              rightIcon={<VolumeUpOutlinedIcon />}
              // w={"250px"}
            />
          </Box>
        </Flex>

        {/* </Flex> */}
      </Popover>
    </Flex>
  );
};
export default JoinRoomMicroPhoneButton;
